<template>
  <main class="asocoins-admin">
    <div class="asocoins-admin__inner">
      <header class="asocoins-admin__header">
        <div class="asocoins-admin__heading">
          <img
            class="asocoins-admin__illustration"
            src="@/assets/images/asocoins/asocoins-falling-coins.webp"
            alt=""
            aria-hidden="true"
          />
          <div>
            <h1>Administración de Asocoins</h1>
            <p class="asocoins-admin__description">Reglas, carteras y asignaciones con historial auditable.</p>
          </div>
        </div>
        <div class="asocoins-admin__header-actions">
          <button class="asocoins-button asocoins-button--outline" type="button" :disabled="loading" @click="refresh">
            <span aria-hidden="true">↻</span> Actualizar
          </button>
        </div>
      </header>

      <div v-if="error" class="asocoins-alert asocoins-alert--error" role="alert">{{ error }}</div>

      <nav class="asocoins-admin__tabs" aria-label="Secciones de administración de Asocoins">
        <button
          class="asocoins-admin__tab"
          :class="{ 'asocoins-admin__tab--active': adminSection === 'wallets' }"
          type="button"
          :aria-selected="adminSection === 'wallets'"
          role="tab"
          @click="adminSection = 'wallets'"
        >
          <span class="asocoins-admin__tab-icon" aria-hidden="true"><v-icon>mdi-wallet</v-icon></span>
          <span><strong>Carteras</strong><small>SOFOMES, saldos y movimientos</small></span>
        </button>
        <button
          class="asocoins-admin__tab"
          :class="{ 'asocoins-admin__tab--active': adminSection === 'rules' }"
          type="button"
          :aria-selected="adminSection === 'rules'"
          role="tab"
          @click="adminSection = 'rules'"
        >
          <span class="asocoins-admin__tab-icon" aria-hidden="true">≡</span>
          <span><strong>Reglas</strong><small>Catálogo y estado operativo</small></span>
        </button>
      </nav>

      <section v-if="adminSection === 'wallets'" class="asocoins-admin__section" role="tabpanel">
        <header class="asocoins-admin__section-header">
          <div>
            <p class="asocoins-overline">Gestión de saldo</p>
            <h2>Carteras de SOFOMES</h2>
            <p class="asocoins-admin__section-description">Selecciona una SOFOM para revisar su saldo, consultar movimientos o registrar un ajuste manual.</p>
          </div>
          <span v-if="selectedSofom" class="asocoins-context-badge">{{ selectedSofom.company || selectedSofom.name || selectedSofom.email }}</span>
        </header>

        <div class="asocoins-grid asocoins-grid--admin">
          <article class="asocoins-card">
            <div class="asocoins-card__header"><h2>SOFOMES de ASOFOM</h2></div>
            <div class="asocoins-card__body">
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-sofom-search">Buscar</label>
                <input id="asocoins-sofom-search" v-model="search" class="asocoins-input" type="search" placeholder="Nombre, correo o empresa" @input="loadSofomes" />
              </div>
              <ul class="asocoins-list asocoins-list--sofomes">
                <li v-for="sofom in sofomes.docs" :key="sofom._id">
                  <button
                    class="asocoins-sofom-option"
                    :class="{ 'asocoins-sofom-option--selected': selectedSofomId === sofom._id }"
                    type="button"
                    @click="selectSofom(sofom)"
                  >
                    <span>
                      <span class="asocoins-sofom-option__name">{{ sofom.company || sofom.name || sofom.email }}</span>
                      <span class="asocoins-sofom-option__meta">{{ sofom.email }} · {{ formatPoints(sofom.availableBalance) }} Asocoins</span>
                    </span>
                    <span class="asocoins-sofom-option__arrow" aria-hidden="true">›</span>
                  </button>
                </li>
                <li v-if="!sofomes.docs.length" class="asocoins-empty">No hay resultados.</li>
              </ul>
            </div>
          </article>

          <article class="asocoins-card asocoins-card--account">
            <div class="asocoins-card__header">
              <h2>{{ selectedSofom ? (selectedSofom.company || selectedSofom.name) : "Detalle de cartera" }}</h2>
            </div>
            <div class="asocoins-card__body">
              <div v-if="!selectedSofomId" class="asocoins-alert asocoins-alert--info">Selecciona una SOFOM para consultar su cartera.</div>
              <div v-else class="asocoins-admin__wallet">
                <div class="asocoins-stat">
                  <p class="asocoins-stat__label">Saldo disponible</p>
                  <p class="asocoins-stat__value">{{ formatPoints(adminWallet && adminWallet.availableBalance) }}</p>
                </div>
                <div class="asocoins-stat">
                  <p class="asocoins-stat__label">Equivalencia</p>
                  <p class="asocoins-stat__value asocoins-stat__value--small">{{ formatMoney((adminWallet && adminWallet.availableBalance) / 10) }} MXN</p>
                </div>
                <div class="asocoins-stat">
                  <p class="asocoins-stat__label">ID del miembro SOFOM</p>
                  <p class="asocoins-stat__value asocoins-stat__value--id">{{ selectedSofomId }}</p>
                </div>
              </div>
            </div>
          </article>
        </div>

        <template v-if="selectedSofomId">
        <div class="asocoins-grid asocoins-grid--admin-lower">
          <article class="asocoins-card">
            <div class="asocoins-card__header"><h2>Asignación manual de Asocoins</h2></div>
            <div class="asocoins-card__body">
              <div class="asocoins-assignment-modes" role="group" aria-label="Modo de asignación">
                <button
                  class="asocoins-button"
                  :class="assignmentMode === 'catalog' ? 'asocoins-button--amber' : 'asocoins-button--outline'"
                  type="button"
                  :aria-pressed="assignmentMode === 'catalog'"
                  @click="setAssignmentMode('catalog')"
                >Por concepto</button>
                <button
                  class="asocoins-button"
                  :class="assignmentMode === 'adjustment' ? 'asocoins-button--amber' : 'asocoins-button--outline'"
                  type="button"
                  :aria-pressed="assignmentMode === 'adjustment'"
                  @click="setAssignmentMode('adjustment')"
                >Personalizada</button>
              </div>

              <form v-if="assignmentMode === 'catalog'" class="asocoins-form" @submit.prevent="prepareAssignmentConfirmation">
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-rule">Ítem del catálogo</label>
                  <select id="asocoins-assignment-rule" v-model="assignment.ruleId" class="asocoins-select" required @change="handleAssignmentRuleChange">
                    <option :value="null" disabled>Selecciona una regla</option>
                    <option v-for="rule in rules" :key="rule._id" :value="rule._id">{{ rule.name }} · {{ formatRuleAmount(rule) }} Asocoins</option>
                  </select>
                </div>
                <div v-if="assignmentUsesQuantity" class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-quantity">Cantidad</label>
                  <input id="asocoins-assignment-quantity" v-model.number="assignment.quantity" class="asocoins-input" type="number" min="1" step="1" required @input="clearPreview" />
                </div>
                <div v-if="assignmentHasCustomAmount" class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-amount">Importe por unidad</label>
                  <input id="asocoins-assignment-amount" v-model.number="assignment.amount" class="asocoins-input" type="number" min="0" step="1" :max="selectedRule.maxAmount" required @input="clearPreview" />
                  <p class="asocoins-field__hint">El total considera la cantidad seleccionada.</p>
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-assignment-note">Nota</label>
                  <textarea id="asocoins-assignment-note" v-model="assignment.note" class="asocoins-textarea" rows="2" @input="clearPreview"></textarea>
                </div>
                <div class="asocoins-actions">
                  <button class="asocoins-button asocoins-button--outline" type="button" :disabled="previewLoading || !assignment.ruleId" @click="preview">
                    {{ previewLoading ? "Calculando…" : "Vista previa" }}
                  </button>
                  <button class="asocoins-button asocoins-button--amber" type="submit" :disabled="loading || previewLoading">
                    {{ loading ? "Aplicando…" : "Aplicar asignación" }}
                  </button>
                </div>
              </form>
              <div v-if="previewData && assignmentMode === 'catalog'" class="asocoins-alert asocoins-alert--info">
                Total calculado: {{ signedPoints(previewData.amount, previewData.direction) }} Asocoins · {{ previewData.direction === 'DEBIT' ? 'débito' : 'abono' }}
              </div>

              <form v-if="assignmentMode === 'adjustment'" class="asocoins-form" @submit.prevent="prepareAdjustmentConfirmation">
                <div class="asocoins-adjustment-directions" role="group" aria-label="Tipo de ajuste manual">
                  <button
                    class="asocoins-button"
                    :class="adjustment.direction === 'CREDIT' ? 'asocoins-button--amber' : 'asocoins-button--outline'"
                    type="button"
                    @click="adjustment.direction = 'CREDIT'"
                  >Agregar</button>
                  <button
                    class="asocoins-button"
                    :class="adjustment.direction === 'DEBIT' ? 'asocoins-button--amber' : 'asocoins-button--outline'"
                    type="button"
                    @click="adjustment.direction = 'DEBIT'"
                  >Restar</button>
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-custom-adjustment-amount">Asocoins</label>
                  <input id="asocoins-custom-adjustment-amount" v-model.number="adjustment.amount" class="asocoins-input" type="number" min="1" step="1" required />
                </div>
                <div class="asocoins-field">
                  <label class="asocoins-field__label" for="asocoins-custom-adjustment-note">Motivo (opcional)</label>
                  <textarea id="asocoins-custom-adjustment-note" v-model="adjustment.note" class="asocoins-textarea" rows="2"></textarea>
                </div>
                <div class="asocoins-actions">
                  <button class="asocoins-button asocoins-button--amber" type="submit" :disabled="loading || !adjustment.amount">
                    {{ adjustment.direction === 'DEBIT' ? 'Continuar para restar' : 'Continuar para agregar' }}
                  </button>
                </div>
              </form>
            </div>
          </article>

          <article class="asocoins-card">
            <div class="asocoins-card__header"><h2>Resumen de operación</h2></div>
            <div class="asocoins-card__body">
              <p class="asocoins-body-copy">Las operaciones se registran en la cartera de la SOFOM seleccionada. La asignación por concepto incluye vista previa antes de confirmar.</p>
              <dl class="asocoins-operation-summary">
                <div><dt>SOFOM seleccionada</dt><dd>{{ selectedSofom ? (selectedSofom.company || selectedSofom.name || selectedSofom.email) : "Ninguna" }}</dd></div>
                <div><dt>Modo</dt><dd>{{ assignmentMode === 'catalog' ? 'Por concepto' : 'Personalizada' }}</dd></div>
                <div><dt>Concepto</dt><dd>{{ assignmentMode === 'catalog' ? (selectedRule ? selectedRule.name : 'Ninguno') : (adjustment.direction === 'DEBIT' ? 'Restar Asocoins' : 'Agregar Asocoins') }}</dd></div>
              </dl>
            </div>
          </article>
        </div>

        <article class="asocoins-card">
          <div class="asocoins-card__header">
            <div>
              <h2>Estado de cuenta · {{ selectedSofomName }}</h2>
              <span class="asocoins-caption">Movimientos de la SOFOM seleccionada</span>
            </div>
          </div>
          <div class="asocoins-card__body">
            <div class="asocoins-filters">
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-type">Tipo</label>
                <select id="asocoins-filter-type" v-model="movementFilters.type" class="asocoins-select" @change="reloadMovements">
                  <option :value="null">Todos</option>
                  <option v-for="type in movementTypes" :key="type" :value="type.value">{{ type.label }}</option>
                </select>
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-concept">Concepto</label>
                <select id="asocoins-filter-concept" v-model="movementFilters.concept" class="asocoins-select" @change="reloadMovements">
                  <option value="">Todos los conceptos</option>
                  <option v-for="concept in conceptOptions" :key="concept.value" :value="concept.value">{{ concept.label }}</option>
                </select>
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-start">Desde</label>
                <input id="asocoins-filter-start" v-model="movementFilters.startDate" class="asocoins-input" type="date" @change="reloadMovements" />
              </div>
              <div class="asocoins-field">
                <label class="asocoins-field__label" for="asocoins-filter-end">Hasta</label>
                <input id="asocoins-filter-end" v-model="movementFilters.endDate" class="asocoins-input" type="date" @change="reloadMovements" />
              </div>
              <button class="asocoins-button asocoins-button--ghost" type="button" @click="clearMovementFilters">Limpiar filtros</button>
            </div>
          </div>
          <div class="asocoins-table-wrap">
            <table class="asocoins-table">
              <thead><tr><th>Fecha</th><th>Concepto</th><th>Origen</th><th class="is-number">Importe</th><th class="is-number">Saldo</th></tr></thead>
              <tbody>
                <tr v-for="movement in adminMovements.docs" :key="movement._id">
                  <td>{{ formatDate(movement.createdAt) }}</td>
                  <td>{{ movement.concept || "—" }}</td>
                  <td>{{ sourceLabel(movement.source) }}</td>
                  <td class="is-number" :class="movement.direction === 'DEBIT' ? 'is-debit' : 'is-credit'">{{ signedPoints(movement.amount, movement.direction) }}</td>
                  <td class="is-number">{{ formatPoints(movement.balanceAfter) }}</td>
                </tr>
                <tr v-if="!adminMovements.docs.length"><td colspan="5" class="asocoins-empty">No hay movimientos.</td></tr>
              </tbody>
            </table>
          </div>
        </article>
        </template>
        <div v-else class="asocoins-selection-hint">Selecciona una SOFOM para habilitar la asignación manual y el estado de cuenta.</div>
      </section>

      <section v-else class="asocoins-admin__section" role="tabpanel">
        <header class="asocoins-admin__section-header">
          <div>
            <p class="asocoins-overline">Configuración auditable</p>
            <h2>Catálogo de reglas</h2>
            <p class="asocoins-admin__section-description">Consulta los importes y el estado de cada regla que alimenta las recompensas de Asocoins.</p>
          </div>
          <div class="asocoins-rules-note"><span class="asocoins-status asocoins-status--active">Activo</span> Las reglas activas pueden utilizarse en eventos y encuestas.</div>
        </header>
        <article class="asocoins-card">
          <div class="asocoins-table-wrap">
            <table class="asocoins-table">
              <thead><tr><th>Concepto</th><th>Categoría</th><th>Importe</th><th>Estado</th></tr></thead>
              <tbody>
                <tr v-for="rule in rules" :key="rule._id">
                  <td><strong>{{ rule.name }}</strong></td>
                  <td>{{ rule.category }}</td>
                  <td>{{ signedPoints(rule.amount, rule.direction) }} Asocoins</td>
                  <td><span class="asocoins-status" :class="{ 'asocoins-status--active': rule.active }">{{ rule.active ? 'Activa' : 'Inactiva' }}</span></td>
                </tr>
                <tr v-if="!rules.length"><td colspan="4" class="asocoins-empty">No hay reglas disponibles.</td></tr>
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </div>

    <v-dialog v-model="confirmationDialog" max-width="540">
      <v-card class="asocoins-adjust-confirm-card">
        <v-card-title class="asocoins-adjust-confirm-card__title">
          <v-icon color="#f2a900" size="34">mdi-coins</v-icon>
          <span>Confirmar movimiento</span>
        </v-card-title>
        <v-card-text class="asocoins-adjust-confirm-card__text">
          <p>{{ confirmationMessage }}</p>
          <small>Esta operación quedará registrada en el estado de cuenta.</small>
        </v-card-text>
        <v-card-actions class="asocoins-adjust-confirm-card__actions">
          <v-spacer />
          <v-btn text :disabled="loading" @click="confirmationDialog = false">Cancelar</v-btn>
          <v-btn color="#1b273e" dark :loading="loading" @click="confirmOperation">
            {{ confirmationButtonLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { getAsocoinsSourceLabel, getAsocoinsTypeLabel } from "@/common/asocoinsPresentation";

export default {
  name: "AsocoinsAdmin",
  data() {
    return {
      search: "",
      adminSection: "wallets",
      assignmentMode: "catalog",
      selectedSofomId: null,
      selectedSofom: null,
      previewLoading: false,
      previewData: null,
      assignment: { ruleId: null, quantity: 1, amount: null, note: "" },
      adjustment: { direction: "CREDIT", amount: null, note: "" },
      confirmationDialog: false,
      confirmationOperation: null,
      movementFilters: { type: null, concept: "", startDate: "", endDate: "" },
      movementTypes: ["CREDIT", "DEBIT", "PENALTY", "EXPIRATION", "ADJUSTMENT"]
        .map((value) => ({ value, label: getAsocoinsTypeLabel(value) })),
    };
  },
  computed: {
    ...mapState("asocoins", ["sofomes", "rules", "adminWallet", "adminMovements", "loading", "error"]),
    selectedSofomName() {
      return this.selectedSofom && (this.selectedSofom.company || this.selectedSofom.name || this.selectedSofom.email)
        ? (this.selectedSofom.company || this.selectedSofom.name || this.selectedSofom.email)
        : "SOFOM";
    },
    selectedRule() {
      return this.rules.find((rule) => rule._id === this.assignment.ruleId) || null;
    },
    conceptOptions() {
      const names = [...new Set(this.rules.map((rule) => rule.name).filter(Boolean))]
        .sort((left, right) => left.localeCompare(right, "es"));
      return [
        ...names.map((name) => ({ value: name, label: name })),
        { value: "__OTHER__", label: "Otros conceptos" },
      ];
    },
    assignmentUsesQuantity() {
      return Boolean(this.selectedRule && ["TICKET", "EVENT", "UNIT"].includes(this.selectedRule.unit));
    },
    assignmentHasCustomAmount() {
      return Boolean(this.selectedRule && (
        this.selectedRule.minAmount !== null && this.selectedRule.minAmount !== undefined
        || this.selectedRule.maxAmount !== null && this.selectedRule.maxAmount !== undefined
      ));
    },
    confirmationMessage() {
      const operation = this.confirmationOperation;
      if (!operation) return "";
      const amount = this.formatPoints(operation.amount);
      if (operation.kind === "catalog") {
        const verb = operation.direction === "DEBIT" ? "restará" : "abonará";
        return `¿Estás seguro de que deseas aplicar el concepto ${operation.concept} a ${operation.sofomName}? Esta operación ${verb} ${amount} Asocoins.`;
      }
      const verb = operation.direction === "DEBIT" ? "restar" : "agregar";
      return `¿Estás seguro de que deseas ${verb} ${amount} Asocoins a ${operation.sofomName}?`;
    },
    confirmationButtonLabel() {
      if (!this.confirmationOperation) return "Confirmar";
      if (this.confirmationOperation.kind === "catalog") return "Sí, aplicar asignación";
      return this.confirmationOperation.direction === "DEBIT" ? "Sí, restar" : "Sí, agregar";
    },
  },
  methods: {
    ...mapActions("asocoins", ["getAdminSofomes", "getAdminRules", "getAdminWallet", "getAdminMovements", "assignAsocoins", "adjustAsocoins"]),
    async refresh() {
      await Promise.all([this.loadSofomes(), this.getAdminRules()]);
      if (this.selectedSofomId) await this.loadAccount(this.selectedSofom);
    },
    loadSofomes() {
      return this.getAdminSofomes({ search: this.search, page: 1, limit: 50 });
    },
    async selectSofom(sofom) {
      this.selectedSofom = sofom;
      this.selectedSofomId = sofom._id;
      this.previewData = null;
      await this.loadAccount(sofom);
    },
    async loadAccount(sofom) {
      if (!sofom || !sofom._id) return;
      await Promise.all([
        this.getAdminWallet(sofom._id),
        this.getAdminMovements({ sofomId: sofom._id, params: { page: 1, limit: 50, ...this.movementFilters } }),
      ]);
    },
    reloadMovements() {
      return this.loadAccount(this.selectedSofom);
    },
    clearMovementFilters() {
      this.movementFilters = { type: null, concept: "", startDate: "", endDate: "" };
      return this.reloadMovements();
    },
    handleAssignmentRuleChange() {
      const rule = this.selectedRule;
      this.assignment.quantity = 1;
      this.assignment.amount = rule && this.ruleHasRange(rule) ? this.ruleDefaultAmount(rule) : null;
      this.clearPreview();
    },
    setAssignmentMode(mode) {
      this.assignmentMode = mode;
      this.clearPreview();
    },
    ruleHasRange(rule) {
      return Boolean(rule && (
        rule.minAmount !== null && rule.minAmount !== undefined
        || rule.maxAmount !== null && rule.maxAmount !== undefined
      ));
    },
    clearPreview() {
      this.previewData = null;
    },
    buildAssignmentPayload(dryRun) {
      const payload = {
        ruleId: this.assignment.ruleId,
        quantity: this.assignmentUsesQuantity ? Number(this.assignment.quantity || 1) : 1,
        note: this.assignment.note,
      };
      if (this.assignmentHasCustomAmount && this.assignment.amount !== null && this.assignment.amount !== "") {
        payload.amount = Number(this.assignment.amount);
      }
      if (dryRun) payload.dryRun = true;
      return payload;
    },
    async fetchAssignmentPreview() {
      if (!this.selectedSofomId || !this.assignment.ruleId) return null;
      this.previewLoading = true;
      try {
        const result = await this.assignAsocoins({
          sofomId: this.selectedSofomId,
          payload: this.buildAssignmentPayload(true),
        });
        this.previewData = result.status ? result.data.preview : null;
        if (!result.status) this.notify("error", result.message);
        return this.previewData;
      } finally {
        this.previewLoading = false;
      }
    },
    async preview() {
      return this.fetchAssignmentPreview();
    },
    async prepareAssignmentConfirmation() {
      if (!this.selectedSofomId || !this.assignment.ruleId) return;
      const preview = await this.fetchAssignmentPreview();
      if (!preview) return;
      this.confirmationOperation = {
        kind: "catalog",
        sofomName: this.selectedSofomName,
        concept: this.assignment.note.trim() || preview.rule.name,
        amount: preview.amount,
        direction: preview.direction,
        payload: { ...this.buildAssignmentPayload(false), expectedAmount: preview.amount },
      };
      this.confirmationDialog = true;
    },
    prepareAdjustmentConfirmation() {
      const amount = Number(this.adjustment.amount);
      if (!Number.isInteger(amount) || amount <= 0 || !this.selectedSofomId) {
        this.notify("error", "El importe debe ser un entero mayor que cero.");
        return;
      }
      this.confirmationOperation = {
        kind: "adjustment",
        sofomName: this.selectedSofomName,
        amount,
        direction: this.adjustment.direction,
      };
      this.confirmationDialog = true;
    },
    async confirmOperation() {
      const operation = this.confirmationOperation;
      if (!operation || !this.selectedSofomId) return;
      const result = operation.kind === "catalog"
        ? await this.assignAsocoins({ sofomId: this.selectedSofomId, payload: operation.payload })
        : await this.adjustAsocoins({
          sofomId: this.selectedSofomId,
          payload: {
            direction: operation.direction,
            amount: operation.amount,
            note: this.adjustment.note,
          },
        });
      if (!result.status) {
        this.confirmationDialog = false;
        this.confirmationOperation = null;
        this.notify("error", result.message);
        if (result.message && result.message.includes("vista previa")) this.clearPreview();
        return;
      }

      this.confirmationDialog = false;
      this.confirmationOperation = null;
      this.notify("success", operation.kind === "catalog" ? "Asignación registrada." : "Ajuste manual registrado.");
      this.adjustment = { direction: "CREDIT", amount: null, note: "" };
      if (operation.kind === "catalog") {
        this.assignment = { ...this.assignment, quantity: 1, amount: this.selectedRule && this.ruleHasRange(this.selectedRule) ? this.ruleDefaultAmount(this.selectedRule) : null, note: "" };
        this.clearPreview();
      }
      await this.refresh();
    },
    notify(color, text) {
      this.$store.commit("notification/show", { color, text: text || "No se pudo completar la operación." });
    },
    ruleDefaultAmount(rule) {
      return rule && rule.amount !== undefined && rule.amount !== null ? Number(rule.amount) : null;
    },
    formatRuleAmount(rule) {
      return this.signedPoints(this.ruleDefaultAmount(rule), rule && rule.direction);
    },
    sourceLabel(source) { return getAsocoinsSourceLabel(source); },
    signedPoints(value, direction) {
      return `${direction === "DEBIT" ? "−" : "+"}${this.formatPoints(value)}`;
    },
    formatPoints(value) { return new Intl.NumberFormat("es-MX").format(Number(value) || 0); },
    formatMoney(value) { return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(value) || 0); },
    formatDate(value) { return value ? new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—"; },
  },
  created() { this.refresh(); },
};
</script>
